/* Noom (naprawa koszyka, etap 2, 05.10.2026): "Kup teraz" sets the EXACT quantity of the chosen variant instead of adding to the cart.
   Plan: tasks/2026-10-04-audyt-stackowanie-koszyka/PLAN-NAPRAWY-FABLE.md (etap 2, sekcja 8). Loaded only where the setting
   `exact_quantity` is on, and used only by <product-form data-direct-checkout="true" data-exact-qty="true"> (assets/hm-product-form.js).
   One click = one fresh cart read, then at most one write (two only when a different line of the same variant must be cleared first):
   - no line of the variant            -> the form's usual cart/add (with the Knee bonus in the same request, as before);
   - one matching line, nothing else   -> nothing to write when it already holds the quantity, else cart/change.js by line key;
   - several lines of the variant      -> cart/update.js by line keys: the line whose properties match the form = quantity, others = 0;
   - only non-matching lines           -> cart/update.js clears them, then the form's usual cart/add.
   Lines carrying `_ocu_*` properties (an accepted One Click Upsell offer) are never changed and never counted. Other products stay.
   The result is verified (variant quantity without OCU lines == selected quantity); otherwise the click fails visibly, never writes again. */
(function () {
  'use strict';
  if (window.HMExactQty) return;

  const OCU_PROPERTY = /^_ocu_/;
  const PROPERTY_FIELD = /^properties\[(.+)\]$/;

  function routeRoot(root) {
    const value = typeof root === 'string' && /^\/(?!\/)[^?#]*$/.test(root) ? root : (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return /^\/(?!\/)[^?#]*$/.test(value) ? value.replace(/\/?$/, '/') : '/';
  }

  function lineProperties(item) {
    const properties = item && item.properties;
    return properties && typeof properties === 'object' && !Array.isArray(properties) ? properties : {};
  }

  function isOcuLine(item) {
    return Object.keys(lineProperties(item)).some((key) => OCU_PROPERTY.test(key));
  }

  // Same normalisation as ProductForm#confirmsCheckoutLine (hm-product-form.js).
  function normalize(value) {
    return JSON.stringify(Object.entries(value || {}).filter(([, v]) => v != null && String(v) !== '')
      .map(([k, v]) => [k, String(v)]).sort(([a], [b]) => a.localeCompare(b)));
  }

  function formProperties(formData) {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(PROPERTY_FIELD);
      if (match && String(value) !== '') properties[match[1]] = String(value);
    }
    return properties;
  }

  function sellingPlanOf(item) {
    return String((item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || '');
  }

  function variantLines(cart, variantId) {
    return (cart && Array.isArray(cart.items) ? cart.items : [])
      .filter((item) => String(item.variant_id) === String(variantId) && !isOcuLine(item));
  }

  function variantQuantity(cart, variantId) {
    return variantLines(cart, variantId).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  // own = lines this form would create (same properties and selling plan); others = remaining lines of the variant (not OCU).
  function classify(cart, variantId, formData) {
    const target = normalize(formProperties(formData));
    const plan = String(formData.get('selling_plan') || '');
    const lines = variantLines(cart, variantId);
    const own = lines.filter((item) => normalize(lineProperties(item)) === target && sellingPlanOf(item) === plan);
    const others = lines.filter((item) => !own.includes(item));
    return { lines, own, others };
  }

  // Same receipt check as ProductForm#validCheckoutCart.
  function validCart(cart) {
    const totals = ['item_count', 'items_subtotal_price', 'original_total_price', 'total_price', 'total_discount', 'total_weight'];
    return !!cart && /^[A-Z]{3}$/.test(cart.currency || '') &&
      totals.every((key) => Number.isFinite(cart[key]) && cart[key] >= 0) && Array.isArray(cart.items) &&
      cart.items.every((item) => item && typeof item.key === 'string' && item.key.trim() &&
        Number.isSafeInteger(item.variant_id) && item.variant_id > 0 &&
        Number.isSafeInteger(item.quantity) && item.quantity > 0);
  }

  function unconfirmed(message, extra) {
    return Object.assign(new Error(message), { unconfirmed: true }, extra || {});
  }

  // One deadline for the whole click (as ProductForm#readDirectCart): no answer in time = unconfirmed, the request is aborted.
  async function request(url, init, deadlineAt) {
    const remaining = deadlineAt - Date.now();
    if (!(remaining > 0)) throw unconfirmed('Checkout confirmation timed out', { name: 'AbortError' });
    const controller = new AbortController();
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = window.setTimeout(() => {
        controller.abort();
        reject(unconfirmed('Checkout confirmation timed out', { name: 'AbortError' }));
      }, remaining);
    });
    try {
      return await Promise.race([
        fetch(url, { credentials: 'same-origin', ...init, signal: controller.signal }).then((response) => response.text().then((text) => {
          let data = null;
          try { data = JSON.parse(text); } catch (_) { data = null; }
          return { status: response.status, ok: response.ok, data, isJson: !!data && typeof data === 'object' };
        })),
        timeout,
      ]);
    } finally { window.clearTimeout(timer); }
  }

  async function readCart(root, deadlineAt) {
    const parsed = await request(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } }, deadlineAt);
    if (!parsed.ok || !parsed.isJson || !validCart(parsed.data)) throw unconfirmed('Cart could not be read before setting the quantity', { status: parsed.status });
    return parsed.data;
  }

  // A JSON 422 on the first write is Shopify's refusal (nothing changed): shown like today's refused add. Anything else is unconfirmed.
  async function write(root, path, payload, deadlineAt, state) {
    const parsed = await request(`${root}${path}`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload),
    }, deadlineAt);
    const data = parsed.data;
    if (parsed.isJson && (!parsed.ok || data.status)) {
      const refused = parsed.status === 422 || (parsed.ok && Number(data.status) === 422);
      if (refused && !state.wrote) throw Object.assign(new Error(data.description || data.message || 'Cart refused the change'), { refused: true, status: 422, response: data });
      throw unconfirmed(`${path} answered ${parsed.status}`, { status: parsed.status, cartChanged: state.wrote });
    }
    if (!parsed.ok || !parsed.isJson || !validCart(data)) throw unconfirmed(`${path} answered ${parsed.status} without a cart`, { status: parsed.status, cartChanged: true });
    state.wrote = true;
    return data;
  }

  // options: { variantId, quantity, formData, deadlineAt, root?, add(state) -> full cart after the form's own add }
  // returns { mutation: 'add'|'none'|'change'|'update'|'replace', cart, before, delta }
  async function reconcile(options) {
    const variantId = String(options.variantId || '');
    const quantity = Number(options.quantity);
    if (!/^\d+$/.test(variantId) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Exact quantity: invalid variant or quantity');
    const formData = options.formData;
    const deadlineAt = options.deadlineAt;
    const root = routeRoot(options.root);
    const state = { wrote: false };
    const current = await readCart(root, deadlineAt);
    const { lines, own, others } = classify(current, variantId, formData);
    const before = lines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    let mutation;
    let cart;
    if (!lines.length) {
      mutation = 'add';
      cart = await options.add(state);
    } else if (own.length === 1 && !others.length && own[0].quantity === quantity) {
      mutation = 'none';
      cart = current;
    } else if (own.length === 1 && !others.length) {
      mutation = 'change';
      cart = await write(root, 'cart/change.js', { id: own[0].key, quantity }, deadlineAt, state);
    } else if (own.length) {
      mutation = 'update';
      const updates = {};
      own.forEach((item, index) => { updates[item.key] = index === 0 ? quantity : 0; });
      others.forEach((item) => { updates[item.key] = 0; });
      cart = await write(root, 'cart/update.js', { updates }, deadlineAt, state);
    } else {
      mutation = 'replace';
      const updates = {};
      others.forEach((item) => { updates[item.key] = 0; });
      const cleared = await write(root, 'cart/update.js', { updates }, deadlineAt, state);
      if (variantQuantity(cleared, variantId) !== 0) throw unconfirmed('Other lines of the variant were not cleared', { cartChanged: true });
      cart = await options.add(state);
    }
    if (!validCart(cart)) throw unconfirmed('Cart after the change is not confirmed', { cartChanged: state.wrote });
    const after = variantQuantity(cart, variantId);
    if (after !== quantity) throw unconfirmed(`Cart holds ${after} of the variant, expected ${quantity}`, { exactMismatch: true, cartChanged: state.wrote });
    return { mutation, cart, before, delta: quantity - before };
  }

  // Only plain single-line product forms (id + quantity + properties) are handled; anything else keeps the ordinary path.
  function supports(formData) {
    if (!formData || typeof formData.entries !== 'function' || typeof fetch !== 'function' || typeof AbortController !== 'function') return false;
    for (const [key] of formData.entries()) if (/^items\[/.test(key)) return false;
    return /^\d+$/.test(String(formData.get('id') || ''));
  }

  function publishCartError(payload) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) publish(PUB_SUB_EVENTS.cartError, payload);
  }

  // The direct-checkout part of ProductForm#onSubmitHandler for exact quantity. `form` is the <product-form>; ctx comes from the
  // shared part of the handler (button already busy, bonus token taken, add request prepared exactly as for the ordinary path).
  function submitProductForm(form, evt, ctx) {
    const { formData, config, deadlineAt, bonus, bonusUI, bonusToken, bundledAdd } = ctx;
    const root = routeRoot();
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;
    const addUrl = (window.routes && window.routes.cart_add_url) || `${root}cart/add`;
    let linesUpdateDeferred = null;
    form.addConfirmed = false;
    // Signal for the cart drawer only (hm-cart-drawer.js, submit capture): while this record exists, a retry click after a failure
    // goes back to this form (one idempotent read + set) instead of the drawer's add-based verification. It is never replayed:
    // hm-product-form.js clears it at the start of every exact click, and a confirmed click clears it before checkout.
    form.rememberCheckout(formData, false);

    // The ordinary add request of the form (same body, same checks as the direct-checkout add in hm-product-form.js).
    const addThroughForm = async (state) => {
      const parsed = await form.readDirectCart(addUrl, config, deadlineAt);
      if (!parsed.isJson) throw unconfirmed(`cart/add answered ${parsed.status} without JSON`, { status: parsed.status });
      let response = parsed.data;
      if (!parsed.ok || response.status) {
        const refused = parsed.status === 422 || (parsed.ok && Number(response.status) === 422);
        if (!refused || bundledAdd || state.wrote) throw unconfirmed('Checkout write is unconfirmed', { status: parsed.status, cartChanged: state.wrote });
        throw Object.assign(new Error(response.description || response.message || 'Cart refused the add'), { refused: true, status: 422, response });
      }
      const addedLines = Array.isArray(response.items) ? response.items : [response];
      if (!addedLines.some((line) => String(line.variant_id || line.id) === String(variantId) && Number(line.quantity) >= quantity)) {
        throw unconfirmed('Paid product was not confirmed before checkout', { cartChanged: true });
      }
      state.wrote = true;
      response = form.cart?.cartFromResponse?.(response) || response;
      if (!form.validCheckoutCart(response)) {
        response = (await form.readDirectCart(`${root}cart.js`, { headers: { Accept: 'application/json' } }, deadlineAt)).data;
        if (!form.validCheckoutCart(response)) throw new Error('Checkout cart is not confirmed');
      }
      return response;
    };

    return reconcile({ variantId, quantity, formData, deadlineAt, root, add: addThroughForm })
      .then(async (result) => {
        let response = result.cart;
        form.addConfirmed = true;
        if (bonusToken && form.cart) window.clearTimeout(form.cart.addingTimer);
        if (bonus?.enabled && !bonusUI) throw new Error('Bonus verification is unavailable');
        // Analytics: an "add" only for a real increase, with the increase as its quantity (a repeated click adds nothing).
        if (result.delta > 0) {
          linesUpdateDeferred = form.createCartLinesUpdateEvent(variantId, result.delta);
          form.resolveCartLinesUpdate(linesUpdateDeferred, response);
        }
        if (bonusToken && bonusUI) {
          response = await bonusUI.settle(response, [], result.mutation === 'add' && bundledAdd ? formData : undefined, { strict: true, deadlineAt });
        }
        if (bonusToken && form.cart) form.cart.pendingAdd = null;
        // The paid quantity and any gift are confirmed before leaving the PDP.
        form.addConfirmed = true;
        form.error = false;
        if (!form.validCheckoutCart(response)) throw new Error('Checkout cart is not confirmed');
        if (!form.confirmsCheckoutLine(formData, response, bonus)) throw new Error('Paid line missing after bonus verification');
        if (variantQuantity(response, variantId) !== quantity) throw unconfirmed('Exact quantity changed during bonus verification', { exactMismatch: true });
        form.clearCheckoutRecovery();
        if (form.cart) {
          form.cart.pendingAdd = null;
          form.cart.setAdding?.(false);
          form.cart.unconfirmed = null;
          form.cart.setNeedsSync?.(false);
          form.cart.crossTabDirty = true;
        }
        form.checkoutNavigating = true;
        window.location.assign(`${root}checkout`);
      })
      .catch((e) => {
        if (e && e.refused) {
          // Shopify refused the first write (JSON 422, e.g. stock): nothing changed. Same handling as the refused add today.
          const response = e.response || {};
          form.clearCheckoutRecovery();
          bonusUI?.cancelCheckout();
          const description = Number(response.status) === 429 ? form.errorText('ratelimit') : response.description;
          publishCartError({
            source: 'product-form',
            kind: 'refused',
            directCheckout: true,
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          form.handleErrorMessage(description);
          form.dispatchCartErrorEvent(description || response.message, 'INVALID');
          form.rejectQuietly(linesUpdateDeferred, new Error(description || response.message));
          const soldOutMessage = form.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          form.submitButton.setAttribute('aria-disabled', true);
          form.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          form.error = true;
          return;
        }
        form.checkoutNavigating = false;
        if (bonusToken && bonusUI) bonusUI.markDirty();
        console.error(e);
        const reason = e && e.name === 'AbortError' ? 'timeout' : e && e.unconfirmed ? (e.status === 429 ? 'ratelimit' : 'html') : 'network';
        const message = form.errorText(reason === 'ratelimit' ? 'ratelimit' : reason === 'network' ? 'network' : 'unconfirmed');
        publishCartError({
          source: 'product-form',
          kind: 'unconfirmed',
          directCheckout: true,
          reason,
          status: e && e.status,
          productVariantId: variantId,
          quantity,
          message,
        });
        form.handleErrorMessage(message);
        form.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
        form.rejectQuietly(linesUpdateDeferred, e);
      })
      .finally(() => {
        if (bonusToken) bonus.end(bonusToken);
        // A confirmed change can alter discounts and line keys in every open tab (same signal as a confirmed add).
        if (form.addConfirmed) {
          try { localStorage.setItem('noom-cart-revision', `${Date.now()}:${Math.random()}`); } catch (_) { /* private mode */ }
        }
        if (!form.checkoutNavigating) {
          window.HMCheckoutFeedback?.set(form, false);
          form.submitButton.classList.remove('loading');
          form.submitButton.removeAttribute('aria-busy');
        }
        if (form.addConfirmed && form.cart && form.cart.classList.contains('is-empty')) form.cart.classList.remove('is-empty');
        if (!form.error && !form.checkoutNavigating) form.submitButton.removeAttribute('aria-disabled');
        const spinnerEnd = form.querySelector('.loading__spinner');
        if (spinnerEnd && !form.checkoutNavigating) spinnerEnd.classList.add('hidden');
        if (typeof CartPerformance !== 'undefined') CartPerformance.measureFromEvent('add:user-action', evt);
      });
  }

  window.HMExactQty = Object.freeze({
    version: '2026-10-05.1',
    reconcile,
    submitProductForm,
    supports,
    classify,
    variantQuantity,
  });
})();
